export const readApiField = <T>(
  row: Record<string, unknown>,
  camel: string,
  pascal: string
): T | undefined => (row[camel] ?? row[pascal]) as T | undefined

export const extractListRows = (response: unknown): unknown[] => {
  if (response == null) {
    return []
  }

  if (Array.isArray(response)) {
    return response
  }

  const raw = response as Record<string, unknown>
  const nestedData = raw.data ?? raw.Data

  if (Array.isArray(nestedData)) {
    return nestedData
  }

  if (nestedData != null && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
    const nestedRows =
      (nestedData as Record<string, unknown>).rows ??
      (nestedData as Record<string, unknown>).Rows

    if (Array.isArray(nestedRows)) {
      return nestedRows
    }
  }

  const rows = raw.rows ?? raw.Rows
  return Array.isArray(rows) ? rows : []
}

export const mapListResponse = <T>(
  response: unknown,
  mapper: (item: unknown) => T | null
): T[] =>
  extractListRows(response)
    .map(mapper)
    .filter((item): item is T => item != null)

export interface MeetingRoomFileDtoLike {
  path: string
  originalName?: string
  fileType?: string
}

export const normalizeMeetingRoomFileDto = (file: unknown): MeetingRoomFileDtoLike | null => {
  if (file == null || typeof file !== 'object') {
    return null
  }

  const row = file as Record<string, unknown>
  const path = readApiField<string>(row, 'path', 'Path')?.trim()

  if (path == null || path === '') {
    return null
  }

  return {
    path,
    originalName:
      readApiField<string>(row, 'originalName', 'OriginalName') ??
      readApiField<string>(row, 'name', 'Name'),
    fileType:
      readApiField<string>(row, 'fileType', 'FileType') ??
      readApiField<string>(row, 'type', 'Type'),
  }
}

export const normalizeMeetingRoomFiles = (files: unknown[] | undefined): MeetingRoomFileDtoLike[] =>
  (files ?? [])
    .map(normalizeMeetingRoomFileDto)
    .filter((file): file is MeetingRoomFileDtoLike => file != null)

const readMeetingRoomFileType = (file: MeetingRoomFileDtoLike): string =>
  (file.fileType ?? '').trim().toLowerCase()

export const partitionMeetingRoomImageFiles = (
  files: MeetingRoomFileDtoLike[] | undefined,
  coverFileType: string,
  sliderFileType: string
): {
  cover: MeetingRoomFileDtoLike | null
  sliders: MeetingRoomFileDtoLike[]
} => {
  const normalizedCoverType = coverFileType.toLowerCase()
  const normalizedSliderType = sliderFileType.toLowerCase()

  let cover: MeetingRoomFileDtoLike | null = null
  const sliders: MeetingRoomFileDtoLike[] = []

  for (const file of files ?? []) {
    const type = readMeetingRoomFileType(file)

    if (type === normalizedCoverType) {
      cover = file
      continue
    }

    if (type === normalizedSliderType) {
      sliders.push(file)
    }
  }

  return { cover, sliders }
}

export const readEquipmentNames = (row: Record<string, unknown>): string[] => {
  const equipments = readApiField<unknown[]>(row, 'equipments', 'Equipments') ?? []

  return equipments
    .map(item => {
      if (item == null || typeof item !== 'object') {
        return ''
      }

      const equipment = item as Record<string, unknown>
      return String(equipment.name ?? equipment.Name ?? '').trim()
    })
    .filter(name => name !== '')
}
